// World Bank API integration for macroeconomic indicators
export interface MacroeconomicIndicator {
  countryId: string;
  countryName: string;
  year: number;
  gdpPerCapita?: number;
  unemploymentRate?: number;
  inflationRate?: number;
  laborForceParticipation?: number;
  grossNationalIncome?: number;
  populationTotal?: number;
  urbanPopulation?: number;
  internetUsers?: number;
  electricityAccess?: number;
  educationExpenditure?: number;
  healthExpenditure?: number;
}

export interface WorldBankResponse {
  indicator: {
    id: string;
    value: string;
  };
  country: {
    id: string;
    value: string;
  };
  value: number | null;
  date: string;
  decimal: number;
}

class WorldBankApiService {
  private baseUrl = 'https://api.worldbank.org/v2';
  private currentYear = '2022';
  
  // Key economic indicators for job market analysis
  private indicators = {
    gdpPerCapita: 'NY.GDP.PCAP.CD',           // GDP per capita (current US$)
    unemploymentRate: 'SL.UEM.TOTL.ZS',      // Unemployment, total (% of total labor force)
    inflationRate: 'FP.CPI.TOTL.ZG',         // Inflation, consumer prices (annual %)
    laborForce: 'SL.TLF.CACT.ZS',            // Labor force participation rate, total (% of total population ages 15+)
    gniPerCapita: 'NY.GNP.PCAP.CD',          // GNI per capita, Atlas method (current US$)
    population: 'SP.POP.TOTL',               // Population, total
    urbanPop: 'SP.URB.TOTL.IN.ZS',           // Urban population (% of total population)
    internetUsers: 'IT.NET.USER.ZS',         // Individuals using the Internet (% of population)
    electricityAccess: 'EG.ELC.ACCS.ZS',     // Access to electricity (% of population)
    educationGDP: 'SE.XPD.TOTL.GD.ZS',      // Government expenditure on education, total (% of GDP)
    healthGDP: 'SH.XPD.CHEX.GD.ZS'          // Current health expenditure (% of GDP)
  };

  async getMacroeconomicData(countryCodes: string[]): Promise<MacroeconomicIndicator[]> {
    const results: MacroeconomicIndicator[] = [];
    
    for (const countryCode of countryCodes) {
      try {
        const countryData = await this.getCountryIndicators(countryCode);
        if (countryData) {
          results.push(countryData);
        }
      } catch (error) {
        console.warn(`Failed to fetch data for ${countryCode}:`, error);
      }
    }
    
    return results;
  }

  async getCountryIndicators(countryCode: string): Promise<MacroeconomicIndicator | null> {
    try {
      // Create indicator request URLs
      const indicatorRequests = Object.entries(this.indicators).map(([key, indicator]) => 
        this.fetchIndicator(countryCode, indicator, key)
      );

      // Fetch all indicators in parallel
      const indicatorResults = await Promise.allSettled(indicatorRequests);
      
      // Process results
      const countryData: Partial<MacroeconomicIndicator> = {
        countryId: countryCode,
        year: parseInt(this.currentYear)
      };

      indicatorResults.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) {
          const [key] = Object.entries(this.indicators)[index];
          const { value, countryName } = result.value;
          
          // Map indicator keys to data properties
          switch (key) {
            case 'gdpPerCapita':
              countryData.gdpPerCapita = value;
              break;
            case 'unemploymentRate':
              countryData.unemploymentRate = value;
              break;
            case 'inflationRate':
              countryData.inflationRate = value;
              break;
            case 'laborForce':
              countryData.laborForceParticipation = value;
              break;
            case 'gniPerCapita':
              countryData.grossNationalIncome = value;
              break;
            case 'population':
              countryData.populationTotal = value;
              break;
            case 'urbanPop':
              countryData.urbanPopulation = value;
              break;
            case 'internetUsers':
              countryData.internetUsers = value;
              break;
            case 'electricityAccess':
              countryData.electricityAccess = value;
              break;
            case 'educationGDP':
              countryData.educationExpenditure = value;
              break;
            case 'healthGDP':
              countryData.healthExpenditure = value;
              break;
          }
          
          if (!countryData.countryName) {
            countryData.countryName = countryName;
          }
        }
      });

      return countryData.countryName ? (countryData as MacroeconomicIndicator) : null;
    } catch (error) {
      console.error(`Error fetching data for ${countryCode}:`, error);
      return null;
    }
  }

  private async fetchIndicator(countryCode: string, indicator: string, key: string): Promise<{ value: number; countryName: string } | null> {
    try {
      // Try multiple years as fallback
      const years = [this.currentYear, '2021', '2020', '2019'];
      
      for (const year of years) {
        const url = `${this.baseUrl}/countries/${countryCode}/indicators/${indicator}?format=json&date=${year}`;
        const response = await fetch(url);
        
        if (!response.ok) continue;
        
        const data = await response.json();
        
        if (Array.isArray(data) && data.length > 1 && Array.isArray(data[1])) {
          const indicatorData = data[1][0] as WorldBankResponse;
          
          if (indicatorData && indicatorData.value !== null) {
            return {
              value: indicatorData.value,
              countryName: indicatorData.country.value
            };
          }
        }
      }
      
      return null;
    } catch (error) {
      console.warn(`Failed to fetch ${indicator} for ${countryCode}:`, error);
      return null;
    }
  }

  // Get country code mapping for existing countries
  getCountryCodeMapping(): { [key: string]: string } {
    return {
      'usa': 'US',
      'uk': 'GB', 
      'germany': 'DE',
      'canada': 'CA',
      'netherlands': 'NL',
      'poland': 'PL',
      'czech-republic': 'CZ',
      'hungary': 'HU',
      'romania': 'RO',
      'slovakia': 'SK',
      'slovenia': 'SI',
      'croatia': 'HR',
      'estonia': 'EE',
      'latvia': 'LV',
      'lithuania': 'LT',
      'sweden': 'SE',
      'norway': 'NO',
      'france': 'FR',
      'spain': 'ES',
      'italy': 'IT',
      'portugal': 'PT',
      'austria': 'AT',
      'belgium': 'BE',
      'denmark': 'DK',
      'finland': 'FI',
      'ireland': 'IE',
      'luxembourg': 'LU',
      'switzerland': 'CH'
    };
  }

  // Enhanced country data with macroeconomic context
  formatMacroeconomicData(indicators: MacroeconomicIndicator[]): { [key: string]: MacroeconomicIndicator } {
    const formatted: { [key: string]: MacroeconomicIndicator } = {};
    
    indicators.forEach(indicator => {
      // Find matching country ID from our mapping
      const countryMapping = this.getCountryCodeMapping();
      const countryId = Object.keys(countryMapping).find(
        key => countryMapping[key] === indicator.countryId
      );
      
      if (countryId) {
        formatted[countryId] = indicator;
      }
    });
    
    return formatted;
  }
}

export const worldBankApi = new WorldBankApiService();
export default worldBankApi;